import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Plus, FileText, HelpCircle, Trash2, Upload, Loader2, Globe } from 'lucide-react';
import { knowledgeAPI } from '../lib/api';

const KnowledgePanel = ({ agentId }) => {
  const [knowledge, setKnowledge] = useState([]);
  const [sources, setSources] = useState([]);  // Prompt 7: Sources list
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('faq');
  
  // FAQ form (legacy - question/answer)
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [addingFaq, setAddingFaq] = useState(false);
  
  // Prompt 7: FAQ ingestion form (name + text)
  const [faqName, setFaqName] = useState('');
  const [faqText, setFaqText] = useState('');
  const [ingestingFaq, setIngestingFaq] = useState(false);
  const [ingestResult, setIngestResult] = useState(null);
  
  // File upload (Prompt 9)
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [fileSourceName, setFileSourceName] = useState('');  // Optional source name
  const [uploadResult, setUploadResult] = useState(null);
  
  // Website ingestion (Prompt 11)
  const [siteUrl, setSiteUrl] = useState('');
  const [siteName, setSiteName] = useState('');
  const [maxPages, setMaxPages] = useState(50);  // Updated default to match backend
  const [maxDepth, setMaxDepth] = useState(4);  // Updated default to match backend
  const [sameDomainOnly, setSameDomainOnly] = useState(true);
  const [ingestingSite, setIngestingSite] = useState(false);
  const [siteResult, setSiteResult] = useState(null);

  const loadKnowledge = useCallback(async () => {
    try {
      const data = await knowledgeAPI.list(agentId);
      // Flatten documents from sources
      const items = [];
      for (const source of data) {
        if (source.documents) {
          for (const doc of source.documents) {
            items.push({
              knowledge_id: source.source_id,
              agent_id: agentId,
              type: source.type,
              content: doc.content,
              metadata: doc.metadata,
              created_at: doc.created_at
            });
          }
        }
      }
      setKnowledge(items);
    } catch (error) {
      console.error('Error loading knowledge:', error);
      setKnowledge([]);
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  const loadSources = useCallback(async () => {
    // Prompt 7: Load sources list
    try {
      const data = await knowledgeAPI.listSources(agentId);
      setSources(data);
    } catch (error) {
      console.error('Error loading sources:', error);
      setSources([]);
    }
  }, [agentId]);

  useEffect(() => {
    loadKnowledge();
    loadSources();  // Prompt 7: Load sources list
  }, [loadKnowledge, loadSources]);

  const handleAddFaq = async () => {
    if (!question.trim() || !answer.trim()) return;
    
    setAddingFaq(true);
    try {
      await knowledgeAPI.addFAQ(agentId, question, answer);
      const item = {
        knowledge_id: `know_${Date.now()}`,
        agent_id: agentId,
        type: 'faq',
        content: `Question: ${question}\nAnswer: ${answer}`,
        metadata: { question, answer },
        created_at: new Date().toISOString()
      };
      setKnowledge([item, ...knowledge]);
      setQuestion('');
      setAnswer('');
      await loadSources();  // Refresh sources list
    } catch (error) {
      console.error('Error adding FAQ:', error);
    } finally {
      setAddingFaq(false);
    }
  };

  const handleIngestFAQ = async () => {
    // Prompt 7: Ingest FAQ with name + text
    if (!faqName.trim() || !faqText.trim()) return;
    
    setIngestingFaq(true);
    setIngestResult(null);
    try {
      const result = await knowledgeAPI.ingestFAQ(agentId, faqName, faqText);
      setIngestResult({
        success: true,
        message: `Ingested successfully! Created ${result.chunks} chunks, rebuilt ${result.vectors_rebuilt} vectors.`,
        chunks: result.chunks,
        vectors_rebuilt: result.vectors_rebuilt
      });
      setFaqName('');
      setFaqText('');
      await loadSources();  // Refresh sources list
      await loadKnowledge();  // Refresh knowledge list
    } catch (error) {
      console.error('Error ingesting FAQ:', error);
      setIngestResult({
        success: false,
        message: error.response?.data?.detail || 'Failed to ingest FAQ'
      });
    } finally {
      setIngestingFaq(false);
    }
  };

  const handleFileUpload = async (files) => {
    // Prompt 9: File upload with ingestion
    if (!files || files.length === 0) return;
    
    setUploading(true);
    setUploadResult(null);
    try {
      const file = files[0];  // Handle single file for now
      const result = await knowledgeAPI.uploadFile(agentId, file, fileSourceName || null);
      
      setUploadResult({
        success: true,
        message: `File uploaded successfully! Created ${result.chunks} chunks, rebuilt ${result.vectors_rebuilt} vectors.`,
        chunks: result.chunks,
        vectors_rebuilt: result.vectors_rebuilt,
        source_id: result.source_id
      });
      
      // Refresh sources and knowledge lists
      await loadSources();
      await loadKnowledge();
      
      // Reset form
      setFileSourceName('');
    } catch (error) {
      console.error('Error uploading file:', error);
      setUploadResult({
        success: false,
        message: error.response?.data?.detail || 'Failed to upload file'
      });
    } finally {
      setUploading(false);
    }
  };

  const handleIngestSite = async () => {
    // Prompt 11: Website ingestion
    if (!siteUrl.trim()) return;
    
    setIngestingSite(true);
    setSiteResult(null);
    try {
      const result = await knowledgeAPI.ingestSite(
        agentId,
        siteUrl,
        siteName || null,
        maxPages,
        maxDepth,
        sameDomainOnly
      );
      
      setSiteResult({
        success: true,
        message: `Website ingested successfully! Fetched ${result.pages_fetched} pages, ingested ${result.pages_ingested} pages, created ${result.chunks} chunks.`,
        pages_fetched: result.pages_fetched,
        pages_ingested: result.pages_ingested,
        chunks: result.chunks,
        vectors_rebuilt: result.vectors_rebuilt,
        report: result.report
      });
      
      // Reset form
      setSiteUrl('');
      setSiteName('');
      setMaxPages(50);  // Reset to new default
      setMaxDepth(4);  // Reset to new default
      setSameDomainOnly(true);
      
      // Refresh sources and knowledge lists
      await loadSources();
      await loadKnowledge();
    } catch (error) {
      console.error('Error ingesting site:', error);
      setSiteResult({
        success: false,
        message: error.response?.data?.detail || 'Failed to ingest website'
      });
    } finally {
      setIngestingSite(false);
    }
  };

  const handleDelete = async (knowledgeId) => {
    try {
      await knowledgeAPI.delete(knowledgeId);
      setKnowledge(knowledge.filter(k => k.knowledge_id !== knowledgeId));
    } catch (error) {
      console.error('Error deleting knowledge:', error);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFileUpload(e.dataTransfer.files);
  };

  const faqItems = knowledge.filter(k => k.type === 'faq');
  const fileItems = knowledge.filter(k => k.type === 'file');

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {/* Add Knowledge */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white">Add Knowledge</CardTitle>
          <CardDescription className="text-slate-400">Train your agent with FAQs or documents</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-slate-800 w-full">
              <TabsTrigger value="faq" className="flex-1 data-[state=active]:bg-slate-700">
                <HelpCircle className="h-4 w-4 mr-2" />
                FAQ
              </TabsTrigger>
              <TabsTrigger value="file" className="flex-1 data-[state=active]:bg-slate-700">
                <FileText className="h-4 w-4 mr-2" />
                File
              </TabsTrigger>
              <TabsTrigger value="site" className="flex-1 data-[state=active]:bg-slate-700">
                <Globe className="h-4 w-4 mr-2" />
                Website
              </TabsTrigger>
            </TabsList>

            <TabsContent value="faq" className="space-y-4 mt-4">
              {/* Prompt 7: New FAQ Ingestion (name + text) */}
              <div className="border-b border-slate-700 pb-4 mb-4">
                <Label className="text-slate-200 text-sm font-semibold mb-2 block">
                  Prompt 7: FAQ Ingestion (Name + Text)
                </Label>
                <div className="space-y-2">
                  <Input
                    value={faqName}
                    onChange={(e) => setFaqName(e.target.value)}
                    placeholder="FAQ Name (e.g., 'Business Hours')"
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                <div className="space-y-2 mt-2">
                  <Textarea
                    value={faqText}
                    onChange={(e) => setFaqText(e.target.value)}
                    placeholder="Enter FAQ text here... (will be chunked and embedded)"
                    className="bg-slate-800 border-slate-700 text-white resize-none"
                    rows={6}
                  />
                </div>
                <Button 
                  onClick={handleIngestFAQ}
                  disabled={!faqName.trim() || !faqText.trim() || ingestingFaq}
                  className="w-full mt-2 bg-indigo-600 hover:bg-indigo-500"
                >
                  {ingestingFaq ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Ingesting...</>
                  ) : (
                    <><Plus className="h-4 w-4 mr-2" /> Ingest FAQ</>
                  )}
                </Button>
                {ingestResult && (
                  <div className={`mt-2 p-2 rounded text-xs ${
                    ingestResult.success 
                      ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' 
                      : 'bg-red-500/10 text-red-400 border border-red-500/20'
                  }`}>
                    {ingestResult.message}
                    {ingestResult.vectors_rebuilt !== undefined && (
                      <div className="mt-1 text-slate-400">
                        Chunks: {ingestResult.chunks}, Vectors: {ingestResult.vectors_rebuilt}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Legacy FAQ form (question/answer) */}
              <div className="border-b border-slate-700 pb-4 mb-4">
                <Label className="text-slate-200 text-sm font-semibold mb-2 block">
                  Legacy: FAQ (Question/Answer)
                </Label>
                <div className="space-y-2">
                  <Input
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    placeholder="What are your business hours?"
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                <div className="space-y-2 mt-2">
                  <Textarea
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    placeholder="We are open Monday to Friday, 9am to 5pm."
                    className="bg-slate-800 border-slate-700 text-white resize-none"
                    rows={4}
                  />
                </div>
                <Button 
                  onClick={handleAddFaq}
                  disabled={!question.trim() || !answer.trim() || addingFaq}
                  className="w-full mt-2 bg-slate-700 hover:bg-slate-600"
                >
                  {addingFaq ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Adding...</>
                  ) : (
                    <><Plus className="h-4 w-4 mr-2" /> Add FAQ (Legacy)</>
                  )}
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="file" className="mt-4">
              {/* Prompt 9: File upload with optional name */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-slate-200 text-sm">Source Name (Optional)</Label>
                  <Input
                    value={fileSourceName}
                    onChange={(e) => setFileSourceName(e.target.value)}
                    placeholder="Enter a name for this source (defaults to filename)"
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    dragOver 
                      ? 'border-indigo-500 bg-indigo-500/10' 
                      : 'border-slate-700 hover:border-slate-600'
                  }`}
                >
                  <input
                    type="file"
                    id="file-upload"
                    className="hidden"
                    accept=".pdf,.docx,.txt"
                    onChange={(e) => handleFileUpload(e.target.files)}
                  />
                  <Upload className="h-10 w-10 text-slate-500 mx-auto mb-4" />
                  <p className="text-slate-400 mb-2">Drag and drop files here, or</p>
                  <Button 
                    variant="outline" 
                    onClick={() => document.getElementById('file-upload').click()}
                    disabled={uploading}
                    className="border-slate-700 text-slate-300"
                  >
                    {uploading ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>
                    ) : (
                      'Browse Files'
                    )}
                  </Button>
                  <p className="text-xs text-slate-500 mt-4">Supported: .pdf, .docx, .txt (max 20MB)</p>
                </div>
                
                {/* Upload result message */}
                {uploadResult && (
                  <div className={`p-3 rounded text-sm ${
                    uploadResult.success 
                      ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' 
                      : 'bg-red-500/10 text-red-400 border border-red-500/20'
                  }`}>
                    {uploadResult.message}
                    {uploadResult.vectors_rebuilt !== undefined && (
                      <div className="mt-1 text-slate-400 text-xs">
                        Chunks: {uploadResult.chunks}, Vectors: {uploadResult.vectors_rebuilt}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </TabsContent>
            
            <TabsContent value="site" className="mt-4">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="site-url" className="text-slate-300">Website URL</Label>
                  <Input
                    id="site-url"
                    type="url"
                    placeholder="https://example.com"
                    value={siteUrl}
                    onChange={(e) => setSiteUrl(e.target.value)}
                    className="bg-slate-800 border-slate-700 text-white mt-1"
                    disabled={ingestingSite}
                  />
                </div>
                
                <div>
                  <Label htmlFor="site-name" className="text-slate-300">Source Name (optional)</Label>
                  <Input
                    id="site-name"
                    type="text"
                    placeholder="My Website"
                    value={siteName}
                    onChange={(e) => setSiteName(e.target.value)}
                    className="bg-slate-800 border-slate-700 text-white mt-1"
                    disabled={ingestingSite}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="max-pages" className="text-slate-300">Max Pages</Label>
                    <Input
                      id="max-pages"
                      type="number"
                      min="1"
                      max="100"
                      value={maxPages}
                      onChange={(e) => setMaxPages(parseInt(e.target.value) || 50)}
                      className="bg-slate-800 border-slate-700 text-white mt-1"
                      disabled={ingestingSite}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="max-depth" className="text-slate-300">Max Depth</Label>
                    <Input
                      id="max-depth"
                      type="number"
                      min="0"
                      max="5"
                      value={maxDepth}
                      onChange={(e) => setMaxDepth(parseInt(e.target.value) || 4)}
                      className="bg-slate-800 border-slate-700 text-white mt-1"
                      disabled={ingestingSite}
                    />
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="same-domain"
                    checked={sameDomainOnly}
                    onChange={(e) => setSameDomainOnly(e.target.checked)}
                    className="rounded bg-slate-800 border-slate-700"
                    disabled={ingestingSite}
                  />
                  <Label htmlFor="same-domain" className="text-slate-300 cursor-pointer">
                    Same domain only
                  </Label>
                </div>
                
                <Button
                  onClick={handleIngestSite}
                  disabled={!siteUrl.trim() || ingestingSite}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  {ingestingSite ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Crawling & Ingesting...</>
                  ) : (
                    'Ingest Website'
                  )}
                </Button>
                
                {siteResult && (
                  <div className={`p-3 rounded ${siteResult.success ? 'bg-green-900/30 border border-green-700' : 'bg-red-900/30 border border-red-700'}`}>
                    <div className="text-sm text-slate-300">{siteResult.message}</div>
                    {siteResult.success && siteResult.report && (
                      <div className="mt-2 text-xs text-slate-400">
                        <div>Fetched: {siteResult.report.fetched?.length || 0}</div>
                        <div>Skipped: {siteResult.report.skipped?.length || 0}</div>
                        <div>Failed: {siteResult.report.failed?.length || 0}</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Knowledge List & Sources */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-white">Knowledge Base</CardTitle>
              <CardDescription className="text-slate-400">
                {knowledge.length} items • {sources.length} sources
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Tabs defaultValue="knowledge" className="w-full">
            <TabsList className="bg-slate-800 w-full rounded-none border-b border-slate-700">
              <TabsTrigger value="knowledge" className="flex-1 data-[state=active]:bg-slate-700">
                Knowledge ({knowledge.length})
              </TabsTrigger>
              <TabsTrigger value="sources" className="flex-1 data-[state=active]:bg-slate-700">
                Sources ({sources.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="knowledge" className="m-0">
              <ScrollArea className="h-[400px]">
                {loading ? (
                  <div className="p-4 space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="animate-pulse bg-slate-800 rounded-lg h-20"></div>
                    ))}
                  </div>
                ) : knowledge.length === 0 ? (
                  <div className="p-8 text-center">
                    <div className="w-12 h-12 rounded-xl bg-slate-800 flex items-center justify-center mx-auto mb-3">
                      <FileText className="h-6 w-6 text-slate-500" />
                    </div>
                    <p className="text-slate-400 text-sm">No knowledge items yet</p>
                    <p className="text-slate-500 text-xs mt-1">Add FAQs or upload files to train your agent</p>
                  </div>
                ) : (
                  <div className="p-4 space-y-3">
                    {knowledge.map((item) => (
                      <div 
                        key={item.knowledge_id} 
                        className="bg-slate-800/50 rounded-lg p-3 group hover:bg-slate-800 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge 
                                variant="outline" 
                                className={item.type === 'faq' 
                                  ? 'border-indigo-500/50 text-indigo-400' 
                                  : 'border-emerald-500/50 text-emerald-400'
                                }
                              >
                                {item.type === 'faq' ? (
                                  <><HelpCircle className="h-3 w-3 mr-1" /> FAQ</>
                                ) : (
                                  <><FileText className="h-3 w-3 mr-1" /> File</>
                                )}
                              </Badge>
                              {item.metadata?.filename && (
                                <span className="text-xs text-slate-400 truncate">
                                  {item.metadata.filename}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-slate-300 line-clamp-2">
                              {item.type === 'faq' ? item.metadata?.question : item.content}
                            </p>
                            {item.type === 'faq' && (
                              <p className="text-xs text-slate-500 line-clamp-1 mt-1">
                                A: {item.metadata?.answer}
                              </p>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(item.knowledge_id)}
                            className="h-8 w-8 text-slate-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>

            <TabsContent value="sources" className="m-0">
              {/* Prompt 7: Sources List */}
              <ScrollArea className="h-[400px]">
                {sources.length === 0 ? (
                  <div className="p-8 text-center">
                    <div className="w-12 h-12 rounded-xl bg-slate-800 flex items-center justify-center mx-auto mb-3">
                      <FileText className="h-6 w-6 text-slate-500" />
                    </div>
                    <p className="text-slate-400 text-sm">No sources yet</p>
                    <p className="text-slate-500 text-xs mt-1">Ingest FAQs to see sources here</p>
                  </div>
                ) : (
                  <div className="p-4 space-y-3">
                    {sources.map((source) => (
                      <div 
                        key={source.source_id} 
                        className="bg-slate-800/50 rounded-lg p-3 group hover:bg-slate-800 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge 
                                variant="outline" 
                                className={
                                  source.status === 'complete' || source.status === 'completed'
                                    ? 'border-emerald-500/50 text-emerald-400'
                                    : source.status === 'processing'
                                    ? 'border-yellow-500/50 text-yellow-400'
                                    : source.status === 'failed'
                                    ? 'border-red-500/50 text-red-400'
                                    : 'border-slate-500/50 text-slate-400'
                                }
                              >
                                {source.status}
                              </Badge>
                              <span className="text-xs text-slate-400">
                                {source.type}
                              </span>
                            </div>
                            <p className="text-sm text-slate-300 font-medium mb-1">
                              {source.name}
                            </p>
                            {source.stats && (
                              <div className="text-xs text-slate-400 space-y-1">
                                <div>Chunks: {source.stats.chunks || source.chunk_count || 0}</div>
                                {source.stats.vectors_rebuilt !== undefined && (
                                  <div>Vectors: {source.stats.vectors_rebuilt}</div>
                                )}
                                {source.stats.embed_model && (
                                  <div>Model: {source.stats.embed_model}</div>
                                )}
                              </div>
                            )}
                            {source.error && (
                              <p className="text-xs text-red-400 mt-1">Error: {source.error}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default KnowledgePanel;
